import React, { useState, useMemo } from 'react';

const FILTROS = [
  { key: 'todos',  label: 'Todos' },
  { key: 'ok',     label: 'Em estoque' },
  { key: 'baixo',  label: 'Estoque baixo' },
  { key: 'vazio',  label: 'Sem estoque' },
];

function statusProduto(p) {
  if (p.quantidade === 0) return 'vazio';
  if (p.quantidade <= 3) return 'baixo';
  return 'ok';
}

export default function TabelaProdutos({ produtos, onEntrada, onSaida, onDelete, onEdit, onReceita }) {
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('todos');

  const lista = useMemo(() => {
    return produtos
      .filter(p => filtro === 'todos' || statusProduto(p) === filtro)
      .filter(p => !busca.trim() || p.nome.toLowerCase().includes(busca.toLowerCase()));
  }, [produtos, filtro, busca]);

  const stats = useMemo(() => ({
    total: produtos.length,
    ok: produtos.filter(p => p.quantidade > 3).length,
    baixo: produtos.filter(p => p.quantidade > 0 && p.quantidade <= 3).length,
    vazio: produtos.filter(p => p.quantidade === 0).length,
  }), [produtos]);

  if (produtos.length === 0) {
    return (
      <div className="mt">
        <span className="mt-ico">📦</span>
        Nenhum produto cadastrado ainda.
        <div style={{ fontSize: 13, marginTop: 6 }}>Clique em "+ Novo produto" para começar.</div>
      </div>
    );
  }

  return (
    <div>
      {/* Stats row */}
      <div className="prod-stats">
        <div className="prod-stat">
          <strong>{stats.total}</strong><span>Total</span>
        </div>
        <div className="prod-stat ok">
          <strong>{stats.ok}</strong><span>Em estoque</span>
        </div>
        {stats.baixo > 0 && (
          <div className="prod-stat baixo">
            <strong>{stats.baixo}</strong><span>Estoque baixo</span>
          </div>
        )}
        {stats.vazio > 0 && (
          <div className="prod-stat vazio">
            <strong>{stats.vazio}</strong><span>Sem estoque</span>
          </div>
        )}
      </div>

      {/* Search + filter */}
      <div className="prod-search">
        <div style={{ position: 'relative', flex: 1 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 15, pointerEvents: 'none' }}>🔍</span>
          <input
            className="inp"
            style={{ paddingLeft: 36 }}
            placeholder="Buscar produto..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
        </div>
      </div>
      <div className="prod-filtros">
        {FILTROS.map(f => (
          <button
            key={f.key}
            className={`btn btn-sm ${filtro === f.key ? 'btn1' : 'btn3'}`}
            onClick={() => setFiltro(f.key)}
          >
            {f.label}
            {f.key !== 'todos' && (
              <span style={{ marginLeft: 4, opacity: .7, fontSize: 11 }}>
                ({f.key === 'ok' ? stats.ok : f.key === 'baixo' ? stats.baixo : stats.vazio})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Product cards */}
      {lista.length === 0 ? (
        <div className="mt" style={{ padding: '24px 0' }}>
          <span className="mt-ico">🔍</span>
          Nenhum produto encontrado.
        </div>
      ) : (
        <div className="prod-grid">
          {lista.map(p => {
            const st = statusProduto(p);
            const custoEfetivo = p.custo_receita != null ? Number(p.custo_receita) : Number(p.preco_custo);
            const margem = Number(p.preco) - custoEfetivo;
            const margemPct = p.preco > 0 ? (margem / Number(p.preco)) * 100 : 0;
            return (
              <div key={p.id} className={`prod-card prod-card--${st}`}>
                {/* Header */}
                <div className="prod-card-header">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div className="prod-card-nome">{p.nome}</div>
                      {p.receita_id && <span className="bd bd-g" style={{ fontSize: 10 }}>📝 Receita</span>}
                    </div>
                    {p.descricao && <div className="prod-card-desc">{p.descricao}</div>}
                  </div>
                  <span className={`bd ${st === 'vazio' ? 'bd-y' : st === 'baixo' ? 'bd-r' : 'bd-g'}`}>
                    {p.quantidade === 0 ? 'Sem estoque' : `${p.quantidade} un.`}
                  </span>
                </div>

                {/* Stock bar */}
                <div className="prod-stock-bar">
                  <div
                    className={`prod-stock-fill prod-stock-fill--${st}`}
                    style={{ width: st === 'vazio' ? '0%' : st === 'baixo' ? '20%' : `${Math.min(100, (p.quantidade / 10) * 100)}%` }}
                  />
                </div>

                {/* Prices */}
                <div className="prod-card-prices">
                  <div className="prod-price-item">
                    <span>{p.receita_id ? 'Custo receita' : 'Custo'}</span>
                    <strong>R$ {custoEfetivo.toFixed(2)}</strong>
                  </div>
                  <div className="prod-price-sep" />
                  <div className="prod-price-item">
                    <span>Venda</span>
                    <strong style={{ color: 'var(--g)' }}>R$ {Number(p.preco).toFixed(2)}</strong>
                  </div>
                  <div className="prod-price-sep" />
                  <div className="prod-price-item">
                    <span>Margem</span>
                    <strong style={{ color: margem >= 0 ? 'var(--g)' : 'var(--r)' }}>
                      R$ {margem.toFixed(2)}
                      <span style={{ fontSize: 10, fontWeight: 600, opacity: .8 }}> ({margemPct.toFixed(0)}%)</span>
                    </strong>
                  </div>
                </div>

                {/* Actions — duas linhas para não estourar em mobile */}
                <div className="prod-card-actions">
                  <div className="prod-card-actions-row">
                    <button className="btn btn1 btn-sm" style={{ flex: 1 }} onClick={() => onEntrada(p)}>+ Estoque</button>
                    <button
                      className="btn btn-sm"
                      style={{ flex: 1, background: st === 'vazio' ? 'var(--c1)' : 'var(--g)', color: st === 'vazio' ? 'var(--c3)' : '#fff', border: 'none' }}
                      disabled={st === 'vazio'}
                      onClick={() => onSaida(p)}
                    >🛒 Vender</button>
                  </div>
                  <div className="prod-card-actions-row">
                    <button className="btn btn3 btn-sm" onClick={() => onReceita(p)} style={{ flex: 1 }}>📝 Receita</button>
                    <button className="btn btn3 btn-sm" onClick={() => onEdit(p)}>✎</button>
                    <button
                      className="btn btn-sm"
                      style={{ background: 'var(--r3)', color: 'var(--r)', border: '1px solid #fecaca' }}
                      onClick={() => onDelete(p)}
                    >✕</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
